import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { GENERATIONAL_TRENDS } from '../constants';
import { Generation } from '../types';
import { getMultiCdnUrl } from '../utils/imageUtils';

interface TheEditProps {
    activeGeneration: Generation | 'all';
}

const TheEdit: React.FC<TheEditProps> = ({ activeGeneration }) => {
    const { t } = useTranslation();

    const filteredItems = activeGeneration === 'all'
        ? GENERATIONAL_TRENDS
        : GENERATIONAL_TRENDS.filter(item => item.generation === activeGeneration);

    return (
        <section className="py-12 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex justify-between items-end mb-12">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2 block font-sans">Curated For You</span>
                        <h2 className="text-4xl text-deep-charcoal dark:text-white font-serif italic">The Weekly Edit</h2>
                    </div>
                    <button className="text-sm font-semibold uppercase tracking-wider underline hover:text-indigo-600 transition-colors font-sans">View All</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {filteredItems.map((item, index) => (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="group cursor-pointer"
                        >
                            <div className="relative overflow-hidden mb-4 aspect-[3/4] rounded-2xl shadow-md">
                                <img
                                    src={getMultiCdnUrl(item.imageUrl, 'BLOG')}
                                    alt={t(item.titleKey)}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute top-4 left-4 bg-[#C0A172] text-white text-[10px] font-black px-3 py-1.5 uppercase tracking-widest shadow-lg rounded-full">
                                    {t(item.tagKey)}
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex justify-center pb-8">
                                    <button className="bg-white text-black px-6 py-2 text-xs font-bold uppercase tracking-widest hover:bg-gray-100 transition shadow-lg rounded-full">
                                        Shop Similar
                                    </button>
                                </div>
                            </div>
                            <h3 className="font-serif text-xl mb-1 dark:text-white line-clamp-1">{t(item.titleKey)}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-sans mb-1 uppercase tracking-tighter">{item.brand}</p>
                            <span className="text-sm font-bold font-sans dark:text-[#C0A172]">{item.price}</span>
                        </motion.div>
                    ))}
                </div>
                {filteredItems.length === 0 && (
                    <div className="text-center py-20">
                        <p className="text-gray-500 dark:text-gray-400 italic">No trends found for this generation yet. Stay tuned!</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default TheEdit;
